import { nanoid } from '@reduxjs/toolkit'
import { cloneDeep, pick } from 'lodash'
import type { ReduxQuoteInfo, ReduxRecapInfo } from 'src/types/apiType'
import { getDeterministicRecapId } from 'src/utils/recapGridUtils/getDeterministicRecapId'
import { shouldIncludeRecapBudgetCostline } from 'src/utils/recapGridUtils/recapCostlineInclusion'

function createNewRecapCostLineWithSortKey(costLine: ReduxRecapInfo) {
	const recapCostLine = pick(costLine, [
		'quoteId',
		'tenantId',
		'activeFlag',
		'freelancers',
		'childFreelancers',
		'categoryId',
		'category',
		'category_fr',
		'categoryPrefix',
		'categoryUuid',
		'categoryLabelValue',
		'subCategoryId',
		'subCategory',
		'subCategory_fr',
		'subCategoryPrefix',
		'subCategorySuffix',
		'subCategoryUuid',
		'subCategoryLabelValue',
		'thirdLevelSection',
		'thirdLevelSectionId',
		'thirdLevelSectionPrefix',
		'thirdLevelSection_fr',
		'thirdLevelSectionUuid',
		'thirdLevelSectionLabelValue',
		'isInternational',
		'editable',
		'costLineType',
		'costLineLabel',
		'language',
		'exchangeEnabled',
		'exchangeRate',
		'payrollTaxEnabled',
		'provisionalPayrollTaxEnabled',
		'actualPayrollTaxEnabled',
		'payrollTaxType',
		'provisionalPayrollTaxType',
		'actualPayrollTaxType',
	]) as ReduxRecapInfo
	// reference to costline in recap info, which was used to create this costline
	// if the reference costLine already has a reference id, it means this line was created from an original costline
	// the same reference id is used on this new costline as well
	// if the reference costLine does not have a reference id, it means this is an original costline
	// we need to use this original costline's id as the referenceCostLineUuid
	recapCostLine.referenceCostLineUuid = costLine.referenceCostLineUuid
		? costLine.referenceCostLineUuid
		: costLine.id
	// attributes related to custom costline
	recapCostLine.id = nanoid()
	recapCostLine.isCustomCostLine = true

	recapCostLine.uuid = recapCostLine.id
	recapCostLine.activeFlag = true
	recapCostLine.tableRowIndex = null
	recapCostLine.item = ''
	recapCostLine.item_fr = ''
	recapCostLine.rate = null
	recapCostLine.rateInEuros = null
	recapCostLine.quantity = null
	recapCostLine.number = null
	recapCostLine.shootNumber = null
	recapCostLine.total = 0
	recapCostLine.totalMargin = null

	recapCostLine.provisionalTotal = null
	recapCostLine.actualTotal = null
	recapCostLine.comments = ''
	recapCostLine.invoiceId = null
	recapCostLine.invoiceDocuments = []
	return recapCostLine
}

function createNewRecapCostLineFrenchApfp(costLine: ReduxRecapInfo) {
	const recapCostLine = pick(costLine, [
		'quoteId',
		'tenantId',
		'activeFlag',
		'freelancers',
		'childFreelancers',
		'categoryId',
		'category',
		'category_fr',
		'categoryPrefix',
		'categoryUuid',
		'categoryLabelValue',
		'subCategoryId',
		'subCategory',
		'subCategory_fr',
		'subCategoryPrefix',
		'subCategorySuffix',
		'subCategoryUuid',
		'subCategoryLabelValue',
		'thirdLevelSection',
		'thirdLevelSectionId',
		'thirdLevelSectionPrefix',
		'thirdLevelSection_fr',
		'thirdLevelSectionUuid',
		'thirdLevelSectionLabelValue',
		'isInternational',
		'editable',
		'costLineType',
		'costLineLabel',
		'language',
		'exchangeEnabled',
		'exchangeRate',
		'payrollTaxEnabled',
		'provisionalPayrollTaxEnabled',
		'actualPayrollTaxEnabled',
		'payrollTaxType',
		'provisionalPayrollTaxType',
		'actualPayrollTaxType',
	]) as ReduxRecapInfo
	// attributes related to custom costline
	recapCostLine.isCustomCostLine = true
	// reference to costline in recap info, which was used to create this costline
	// if the reference costLine already has a reference id, it means this line was created from an original costline
	// the same reference id is used on this new costline as well
	// if the reference costLine does not have a reference id, it means this is an original costline
	// we need to use this original costline's id as the referenceCostLineUuid
	recapCostLine.referenceCostLineUuid = costLine.referenceCostLineUuid
		? costLine.referenceCostLineUuid
		: costLine.id

	recapCostLine.uuid = null
	recapCostLine.activeFlag = true
	recapCostLine.tableRowIndex = null
	recapCostLine.item = ''
	recapCostLine.item_fr = ''
	recapCostLine.rate = null
	recapCostLine.rateInEuros = null
	recapCostLine.quantity = null
	recapCostLine.number = null
	recapCostLine.shootNumber = null
	recapCostLine.total = 0
	recapCostLine.totalMargin = null

	recapCostLine.provisionalTotal = null
	recapCostLine.actualTotal = null
	recapCostLine.comments = ''
	recapCostLine.invoiceId = null
	recapCostLine.invoiceDocuments = []
	return recapCostLine
}

function createRecapCostLine(costLine: ReduxQuoteInfo, quoteId: string) {
	const recapCostLine = pick(costLine, [
		'id',
		'categoryId',
		'category',
		'category_fr',
		'categoryPrefix',
		'categoryUuid',
		'subCategoryId',
		'subCategory',
		'subCategory_fr',
		'subCategoryPrefix',
		'subCategorySuffix',
		'subCategoryUuid',
		'item',
		'item_fr',
		'isInternational',
		'editable',
		'costLineType',
		'costLineLabel',
		'language',
		'rate',
		'exchangeEnabled',
		'exchangeRate',
		'rateInEuros',
		'quantity',
		'number',
		'shootNumber',
		'total',
		'totalMargin',
		'payrollTaxEnabled',
		'payrollTaxType',
		'thirdLevelSection',
		'thirdLevelSectionId',
		'thirdLevelSectionPrefix',
		'thirdLevelSection_fr',
		'thirdLevelSectionUuid',
		'thirdLevelSectionLabelValue',
		'costlineUuid',
	]) as ReduxRecapInfo
	recapCostLine.uuid = recapCostLine.id
	// Deterministic id (shared with the masterQuote Lambda) so a concurrent
	// create for the same budget line is rejected by the resolver's
	// attribute_not_exists(id).
	recapCostLine.id = getDeterministicRecapId(quoteId, costLine.id)
	recapCostLine.activeFlag = true
	recapCostLine.isCustomCostLine = false
	recapCostLine.referenceCostLineUuid = null
	recapCostLine.provisionalTotal = null
	recapCostLine.provisionalPayrollTaxEnabled = costLine.payrollTaxEnabled
	recapCostLine.actualTotal = null
	recapCostLine.actualPayrollTaxEnabled = costLine.payrollTaxEnabled
	recapCostLine.provisionalPayrollTaxType = null
	recapCostLine.actualPayrollTaxType = null
	recapCostLine.comments = ''
	recapCostLine.invoiceId = null
	recapCostLine.invoiceDocuments = []
	if (recapCostLine.payrollTaxEnabled === 'Yes') {
		recapCostLine.provisionalPayrollTaxType = costLine.payrollTaxType
		recapCostLine.actualPayrollTaxType = costLine.payrollTaxType
	}
	return recapCostLine
}

function createRecapInfoCostLineIdsAndLabelsFrenchApfp(
	costLines: ReduxRecapInfo[],
	quoteId: string,
	tenantId: string,
	childFreelancers: string[]
) {
	let costLineLabelIndex = 100
	const costLineLabelSuffix = 'A'
	const costLineLabelSuffixInternational = 'B'
	return costLines.map((c, i) => {
		if (c.activeFlag) {
			c.tableRowIndex = i
			c.quoteId = quoteId
			c.tenantId = tenantId
			c.freelancers = ['dummyId']
			c.childFreelancers = [...childFreelancers]
			c.offlineIds = ['dummyId']
			if (c.costLineType === 'costLine') {
				c.costLineLabel = c.isInternational
					? `${costLineLabelIndex}${costLineLabelSuffixInternational}`
					: `${costLineLabelIndex}${costLineLabelSuffix}`
				costLineLabelIndex++
			}
		}
		return c
	})
}

function createRecapInfoFrenchApfp(
	quoteInfo: ReduxQuoteInfo[],
	quoteId: string,
	tenantId: string,
	childFreelancers: string[]
) {
	if (!quoteInfo) return null

	const finalRecapInfo = quoteInfo.reduce(
		(recapInfo: ReduxRecapInfo[], costLine) => {
			if (
				shouldIncludeRecapBudgetCostline(costLine) ||
				costLine.category === 'Payroll Taxes' ||
				costLine.category === 'Payroll Taxes - EXE'
			) {
				const recapCostLine = createRecapCostLine(costLine, quoteId)
				recapInfo.push(recapCostLine)
			}
			return recapInfo
		},
		[]
	)
	return createRecapInfoCostLineIdsAndLabelsFrenchApfp(
		finalRecapInfo,
		quoteId,
		tenantId,
		childFreelancers
	)
}

function refreshRecapInfoFrenchApfp(
	quoteInfo: ReduxQuoteInfo[],
	recapInfo: ReduxRecapInfo[],
	quoteId: string,
	tenantId: string,
	childFreelancers: string[]
) {
	const oldRecapInfo = cloneDeep(recapInfo)
	// Checks if quoteInfo exists
	const newRecapInfo = quoteInfo.reduce(
		(recapInfoArray: ReduxRecapInfo[], quoteInfoCostLine) => {
			const oldRecapCostLine = oldRecapInfo.find(
				(val) => val.uuid && val.uuid === quoteInfoCostLine.id
			)
			if (shouldIncludeRecapBudgetCostline(quoteInfoCostLine)) {
				let newRecapCostLine
				// If old recap costline exists just update the old costline
				if (oldRecapCostLine) {
					const {
						quantity,
						number,
						shootNumber,
						rateInEuros,
						rate,
						total,
						totalMargin,
						payrollTaxEnabled,
						payrollTaxType,
					} = quoteInfoCostLine
					const normalizedTotal = total ?? 0
					newRecapCostLine = {
						...oldRecapCostLine,
						rateInEuros,
						quantity,
						number,
						shootNumber,
						rate,
						total: normalizedTotal,
						totalMargin,
						payrollTaxEnabled,
						payrollTaxType,
						activeFlag: true,
					}
					if (
						oldRecapCostLine.actualPayrollTaxEnabled &&
						!oldRecapCostLine.provisionalPayrollTaxEnabled
					) {
						newRecapCostLine.provisionalPayrollTaxEnabled =
							oldRecapCostLine.actualPayrollTaxEnabled
					}
					if (
						newRecapCostLine.payrollTaxEnabled === 'Yes' &&
						oldRecapCostLine.provisionalPayrollTaxEnabled ===
							'Yes' &&
						!oldRecapCostLine.provisionalPayrollTaxType
					) {
						newRecapCostLine.provisionalPayrollTaxType =
							newRecapCostLine.payrollTaxType
					}
					if (
						newRecapCostLine.payrollTaxEnabled === 'Yes' &&
						oldRecapCostLine.actualPayrollTaxEnabled === 'Yes' &&
						!oldRecapCostLine.actualPayrollTaxType
					) {
						newRecapCostLine.actualPayrollTaxType =
							newRecapCostLine.payrollTaxType
					}
					recapInfoArray.push(newRecapCostLine)
					// find the custom costlines that might have been created with this old cost line as a reference
					const customCostLinesWithOldCostLineAsReference =
						oldRecapInfo.filter(
							(cl) =>
								cl.isCustomCostLine &&
								cl.referenceCostLineUuid === oldRecapCostLine.id
						)
					recapInfoArray = recapInfoArray.concat(
						customCostLinesWithOldCostLineAsReference
					)
				} else {
					newRecapCostLine = createRecapCostLine(
						quoteInfoCostLine,
						quoteId
					)
					recapInfoArray.push(newRecapCostLine)
				}
			} else if (oldRecapCostLine && !quoteInfoCostLine.total) {
				// * If oldRecapCostLine exists and budget costLine.total is 0 or null we set recap costline to total to 0
				const customCostLinesWithOldCostLineAsReference =
					oldRecapInfo.filter(
						(cl) =>
							cl.isCustomCostLine &&
							cl.referenceCostLineUuid === oldRecapCostLine.id
					)
				const newCostLine = { ...oldRecapCostLine, total: 0 }
				recapInfoArray.push(newCostLine)
				if (customCostLinesWithOldCostLineAsReference.length > 0) {
					recapInfoArray = recapInfoArray.concat(
						customCostLinesWithOldCostLineAsReference
					)
				}
			} else {
				// * Else if costLine.total = 0 & oldCostLine also doesn't exist we check if the row belongs to payroll taxes  and add that to recap
				if (quoteInfoCostLine.category === 'Payroll Taxes') {
					const newCostLine = createRecapCostLine(
						quoteInfoCostLine,
						quoteId
					)
					recapInfoArray.push(newCostLine)
				}
			}
			return recapInfoArray
		},
		[]
	)
	return createRecapInfoCostLineIdsAndLabelsFrenchApfp(
		newRecapInfo,
		quoteId,
		tenantId,
		childFreelancers
	)
}

export {
	createNewRecapCostLineFrenchApfp,
	createNewRecapCostLineWithSortKey,
	createRecapInfoCostLineIdsAndLabelsFrenchApfp,
	createRecapInfoFrenchApfp,
	refreshRecapInfoFrenchApfp,
}
